import React, { memo, useState, useMemo, useEffect } from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useParams } from "react-router-dom";
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
} from "react-virtualized";
import { useSelector, useDispatch } from "react-redux";
import usePermissions from "../../../hooks/usePermissions";
import { uniq, filter, isEmpty } from "lodash";
import CommonDialog from "../../../components/CommonDialog";
import CustomBreadcrumb from "../CustomBreadcrumb";
import SettingsForm from "../SettingsForm";
import useRequest from "../../../hooks/useRequest";
import FileExport from "../../../components/FileExport";
import CustomDrawer from "../../../components/CustomDrawer";
import CustomFloatingButton from "../../../components/CustomFloatingButton";
import useStableCallback from "../../../hooks/useStableCallback";
import {
  ENTITY_TYPES,
  NOTE_TYPE_VALUE,
  KeepCacheAsIs,
  singleValue,
  getPogValue,
  orderStatus,
  isBulkFootage,
  isResetSelectionForFilter,
} from "../../../utils";
import CustomListContainer from "../CustomListContainer";
import StoreCreditSummary from "../StoreCreditSummary";
import Total from "../Total";
import {
  showNotifier,
  resetItemFilters,
  setGlobalLoader,
} from "../../../store/globalSlice";
import PermissionsGate from "../../../permissions/PermissionsGate";
import { groupBy } from "../DepartmentSearch";
import { SCOPES } from "../../../permissions/permissions-map";
import StickyHeader from "../StickyHeader";
import PogFilter from "../PogFilter";
import {
  prepareInitialData,
  prepareSettingsSaveData,
  postSettingsSaveUpdateData,
  prepareSaveDatesData,
  postSaveDatesUpdateData,
  prepareDeleteDatesData,
  postDeleteDatesUpdateData,
  saveUpdatedPogs,
  checkPogsBeforeSave,
  prepareCreatePOData,
  postCreatePOUpdateData,
  updateDuplicatePogNames,
  skipPogSummaryUpdate,
  postPogLevelUpdateData,
} from "./prepareData";
import { validPog } from "../../../utils/pogValidator";
import { isItemDepartment } from "../../../utils/deptValidator";
import filterHandler from "../../../utils/filterHandler";
import configData from "../../../config.js";
import { notesApi } from "../../../services/notesApi";
import { setDepartmentName } from "../../../store/itemPogSummarySlice";
import { useLazyGetTotalQuery } from "../../../services/totalApi";
import CustomPOG from "../CustomPOG";
import CollapsedPogView from "../CollapsedPogView";
import ExpandedPogView from "../ExpandedPogView";
import ReleaseStatusChip from "../ReleaseStatusChip";
import CustomNoResult from "../CustomNoResult";

const BreadcrumbHeaderSx = { display: "flex", justifyContent: "space-between" };

const OVERSCAN_ROW_COUNT = 10;
const pogCache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 30,
});

export const resetPogFieldsOnSelection = (field, pog, originalAdjLf) => {
  if (field === "Selected") {
    let currentPog = { ...pog };
    // If selection is made from value 'Y' to 'E'/'N' then reset all values.
    if (pog[field] !== singleValue.yes) {
      currentPog = {
        ...currentPog,
        AdjLineal:
          pog[field] === singleValue.no ? originalAdjLf : currentPog?.AdjLineal,
        CoreDiscount: 0,
        NonCoreDiscount: 0,
        DatingDays: 0,
        DiscMultiplier: 1,
        QtyMultiplier: 1,
        ExtCost: 0,
        AdjCost: 0,
        AdjQty: 0,
        ReleaseStatus: "",
        ReleaseDate: null,
        PONumber: "",
        CustomCoreDiscount: false,
        CustomDatingDays: false,
        CustomDiscMultiplier: false,
        CustomNonCoreDiscount: false,
        CustomQtyMultiplier: false,
      };
    }
    return currentPog;
  }
  return pog;
};

function Pogs() {
  const dispatch = useDispatch();
  const { requestMaker } = useRequest();
  const { departmentLetter } = useParams();
  const { hasPermission } = usePermissions();
  const [getTotal] = useLazyGetTotalQuery();
  const [selectedOriginalPogNames, setSelectedOriginalPogNames] = useState([]);
  const [selectedPogNames, setSelectedPogNames] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [pogsLoading, setPogsLoading] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  // selections are those which items user selected using checkbox, it contains item id only.
  const [selections, setSelections] = useState([]);
  // valid selections are those which shows valid items to be eligible for selection, it contains entire item object.
  const [validSelections, setValidSelections] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const user = useSelector((store) => store.global.user);
  const store = useSelector((store) => store.global.store);
  const noteSaveType = useSelector((store) => store.notes.saveType);
  const pogFilters = useSelector((store) => store.global.pogFilters);
  const departmentFilters = useSelector(
    (store) => store.global.departmentFilters
  );

  const isItemsDepartment = isItemDepartment({
    DepartmentLetter: departmentLetter,
  });

  const isRowLoaded = ({ index }) =>
    index < filteredData.length && !filteredData[index]?.virtualLoading;

  const loadMoreRows = ({ startIndex, stopIndex }) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const d = [...filteredData];
        for (let idx = startIndex; idx <= stopIndex; idx++) {
          d[idx].virtualLoading = false;
        }
        setFilteredData(d);
        resolve();
      }, 800);
    });
  };

  const { data: plannerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.planner,
      storeId: store?.id,
    });

  const { data: retailerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.retailer,
      storeId: store?.id,
    });

  const plannerNoteOptions = useMemo(() => {
    return plannerNotesData?.map((note) => ({
      ...note,
      title: note.Notes,
      id: note.NotesId,
    }));
  }, [plannerNotesData]);

  const retailerNoteOptions = useMemo(() => {
    const list = retailerNotesData?.map((note) => ({
      ...note,
      title: note.Notes,
      id: note.NotesId,
    }));
    return list;
  }, [retailerNotesData]);

  const getPlannerValue = (pog) => {
    return (
      plannerNoteOptions?.find((pn) => pn.id === Number(pog?.PlannerNotes)) ||
      ""
    );
  };

  const memoizedRetailerValue = useMemo(() => {
    return (
      retailerNoteOptions?.find(
        (pn) => pn.id === Number(data?.RetailerNotes)
      ) || ""
    );
  }, [data?.RetailerNotes, retailerNoteOptions]);

  const updateDuplicates = useStableCallback(() => {
    requestMaker({
      method: "post",
      url: configData.CheckDuplicatePOGs,
      data: {
        storeId: store.id,
      },
    }).then((data) => {
      const list = data?.data || [];
      setSelectedOriginalPogNames(list);
      setSelectedPogNames(list);
    });
  });

  useEffect(() => {
    dispatch(resetItemFilters());
    // Fetch already selected POG name list.
    updateDuplicates();

    const pog = getPogValue(
      departmentFilters?.planogramValue,
      departmentFilters?.planogram
    );
    const filterValues = {
      storeId: store.id,
      departmentLetter,
      category: departmentFilters?.category || null,
      planogramValue: pog || null,
      vendorName: departmentFilters?.vendorName || null,
      skuNumber: departmentFilters?._sku || null,
    };

    // Fetch POG list
    requestMaker({
      method: "post",
      url: configData.GetPogList,
      data: filterValues,
    })
      .then((data) => {
        const fetchedData = data?.data || [];
        const pogList = prepareInitialData(fetchedData);
        // Needed for breadcrumb to render department name breadcrumb nav link.
        if (pogList.length > 0) {
          dispatch(setDepartmentName(pogList[0]?.DepartmentName));
        }
        const updatedItems = filterHandler(
          pogFilters,
          pogList,
          pogList,
          ENTITY_TYPES.planogram
        );
        setValidSelections(updatedItems.filter((pog) => validPog(pog)));
        setFilteredData(updatedItems);
        setData(pogList);
        setOriginalData(pogList);
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        setPogsLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  const toggleDrawer = useStableCallback((open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setIsDrawerOpen(open);
  });

  const handleProcessData = useStableCallback(
    (id, PogName) =>
      ({
        field,
        value,
        isPogChecked = false,
        isPogSelected = singleValue.no,
      }) => {
        // Check based on Selection N/Y OR Checked and update duplicate POG name.
        updateDuplicatePogNames({
          field,
          value,
          id,
          PogName,
          setSelectedPogNames,
          isPogSelected,
          isPogChecked,
          originalData,
        });

        let originalAdjLf = 0,
          index;
        if (field === "Selected") {
          for (let i = 0, len = originalData.length; i < len; i++) {
            const p = originalData[i] || {};
            if (p?.PlanogramNumber === id) {
              index = i;
              break;
            }
          }
          if (index || index === 0) {
            originalAdjLf = originalData[index]?.LinealFootage;
          }
        }

        const getUpdatedData = (originalAdjLf) => (oldPogs) => {
          const updatedData = [...oldPogs];
          let index = -1;
          for (let i = 0, len = oldPogs.length; i < len; i++) {
            const p = oldPogs[i] || {};
            if (p?.PlanogramNumber === id) {
              index = i;
              break;
            }
          }
          let pog = oldPogs[index];
          pog = {
            ...resetPogFieldsOnSelection(
              field,
              {
                ...pog,
                [field]: value,
              },
              originalAdjLf
            ),
          };
          updatedData[index] = { ...pog };
          return updatedData;
        };

        setFilteredData(getUpdatedData(originalAdjLf));
        setData(getUpdatedData(originalAdjLf));

        // Handles only POG to Select All auto selection functionality.
        if (field === "selection") {
          if (value) {
            setSelections((oldVal) => {
              const updatedSelections = uniq([...oldVal, id]);
              setSelectAll(updatedSelections.length === validSelections.length);
              return updatedSelections;
            });
          } else {
            setSelections((oldVal) => {
              return filter(oldVal, (itemId) => itemId !== id);
            });
            setSelectAll(false);
          }
        }
      }
  );

  const handleSelectAll = useStableCallback((event) => {
    const checked = event?.target?.checked;
    setFilteredData((oldData) => {
      return oldData?.map((pog) => {
        if (validPog(pog)) {
          checked &&
            setSelections((oldSel) => uniq([...oldSel, pog?.PlanogramNumber]));
          return {
            ...pog,
            selection: checked,
          };
        }
        return pog;
      });
    });

    setData((oldData) => {
      return oldData?.map((pog) => {
        // Only filtered pog set selection value.
        const pogFound = filteredData.find(
          (fp) => fp?.PlanogramNumber === pog?.PlanogramNumber
        );
        if (pogFound && validPog(pog)) {
          checked &&
            setSelections((oldSel) => uniq([...oldSel, pog?.PlanogramNumber]));
          return {
            ...pog,
            selection: checked,
          };
        }
        return pog;
      });
    });

    if (!checked) {
      setSelections([]);
    }
    setSelectAll(!selectAll);
  });

  const handleFilter = useStableCallback(() => {
    const updatedData = filterHandler(
      pogFilters,
      data,
      originalData,
      ENTITY_TYPES.planogram
    );
    const validPogs = updatedData.filter((pog) => validPog(pog));
    pogCache.clearAll();
    setValidSelections(validPogs);
    setFilteredData(updatedData);
    const selectionCount = updatedData.filter((i) => !!i?.selection);
    if (
      selectionCount.length === selections.length &&
      selections.length === updatedData.length &&
      selections.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  });

  const handleFilterReset = useStableCallback(() => {
    const updatedData = filterHandler(
      pogFilters,
      data,
      originalData,
      ENTITY_TYPES.planogram
    );
    const validPogs = updatedData.filter((pog) => validPog(pog));
    pogCache.clearAll();
    setValidSelections(validPogs);
    setFilteredData(updatedData);
    if (selectAll) {
      const selectionCount = updatedData.filter((i) => i?.selection);
      if (
        selectionCount.length === selections.length &&
        selections.length === updatedData.length &&
        selections.length
      ) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }
    }
  });

  const handleSortChange = useStableCallback(() => {
    const updatedData = filterHandler(
      pogFilters,
      filteredData,
      originalData,
      ENTITY_TYPES.planogram
    );
    const validItems = updatedData.filter((pog) => validPog(pog));
    setValidSelections(validItems);
    setFilteredData(KeepCacheAsIs(filteredData, updatedData));
  });

  const handleItemReset = useStableCallback(() => {
    setShowResetDialog(true);
  });

  const handleResetDialogClose = useStableCallback(() => {
    setShowResetDialog(false);
  });

  const handleResetDialogDone = useStableCallback(() => {
    const updatedData = filterHandler(
      pogFilters,
      originalData,
      originalData,
      ENTITY_TYPES.planogram
    );
    const validPogs = updatedData.filter((pog) => validPog(pog));
    setValidSelections(validPogs);
    setFilteredData(() => KeepCacheAsIs(filteredData, updatedData));
    setData(() => originalData);
    setSelectAll(false);
    setSelections([]);
    setSelectedPogNames(selectedOriginalPogNames);
    handleResetDialogClose();
    window.scrollTo(0, 0);
  });

  const handlePogLevelSave = useStableCallback((selectedPog) => () => {
    const originalPog = originalData?.find(
      (p) => p?.PlanogramNumber === selectedPog?.PlanogramNumber
    );
    if (selectedPog?.Selected === singleValue.no) {
      // Allowing saving POG with value "N" only if either of notes values are present otherwise not.
      if (
        originalPog?.Selected === singleValue.no &&
        !originalPog?.RetailerNotes &&
        !selectedPog?.RetailerNotes &&
        !originalPog?.PlannerNotes &&
        !selectedPog?.PlannerNotes
      ) {
        dispatch(
          showNotifier({
            message:
              "This POG is marked as selected 'N'. Please verify 'Sel' field.",
            severity: "error",
          })
        );
        return false;
      }
    }
    const updatedPogDetails = {
      storeId: store.id,
      departmentLetter,
      vendorName: departmentFilters?.vendorName || null,
      skuNumber: departmentFilters?._sku || null,
      userId: user?.email,
      pogList: [
        {
          pogNumber: selectedPog?.PlanogramNumber,
          selected: selectedPog?.Selected,
          prevSelected: selectedPog?.prevSelected,
          plannerNotesId: selectedPog?.PlannerNotes || null,
          retailerNotesId: selectedPog?.RetailerNotes || null,
          adjLineal: isBulkFootage(selectedPog?.AdjLineal)
            ? null
            : selectedPog?.AdjLineal,
          qtyMultiplier: !selectedPog?.CustomQtyMultiplier
            ? selectedPog?.QtyMultiplier || 1
            : null,
          discMultiplier: !selectedPog?.CustomDiscMultiplier
            ? selectedPog?.DiscMultiplier || 1
            : null,
          coreDiscount: !selectedPog?.CustomCoreDiscount
            ? selectedPog?.CoreDiscount || 0
            : null,
          nonCoreDiscount: !selectedPog?.CustomNonCoreDiscount
            ? selectedPog?.NonCoreDiscount || 0
            : null,
          datingDays: !selectedPog?.CustomDatingDays
            ? selectedPog?.DatingDays || 0
            : null,
        },
      ],
    };
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SavePogList,
      data: updatedPogDetails,
    })
      .then((res) => {
        if (res?.data?.length) {
          const updatedPog = res?.data[0] || {};
          let isResetSelection = false;
          if ([groupBy[4], groupBy[5]].includes(departmentFilters.viewBy)) {
            isResetSelection = isResetSelectionForFilter(
              departmentFilters,
              updatedPog?.AdjQty || 0
            );
          }
          const postSaveUpdatedItems = postPogLevelUpdateData(
            {
              ...selectedPog,
              prevSelected: selectedPog?.Selected,
              Selected:
                !isResetSelection &&
                [groupBy[4], groupBy[5]].includes(departmentFilters.viewBy)
                  ? singleValue.yes
                  : selectedPog?.Selected,
            },
            updatedPog,
            originalPog,
            filteredData
          );
          setData(saveUpdatedPogs(postSaveUpdatedItems));
          setOriginalData(saveUpdatedPogs(postSaveUpdatedItems));
          setFilteredData((prevState) => {
            const getUpdates = saveUpdatedPogs(postSaveUpdatedItems);
            const _partialUpdates = getUpdates(prevState);
            const updatedData = filterHandler(
              pogFilters,
              _partialUpdates,
              originalData,
              ENTITY_TYPES.planogram
            );
            return updatedData;
          });
          setSelectAll(false);
          setSelections([]);
          dispatch(
            showNotifier({ message: "POG details saved successfully." })
          );
          getTotal({ storeId: store?.id, type: "" });
          updateDuplicates();
        }
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const handleSettingsSave = useStableCallback((formData) => {
    const isValidSaveData = checkPogsBeforeSave(
      dispatch,
      showNotifier,
      formData.selected === singleValue.yes ? true : false,
      filteredData,
      selections,
      originalData,
      formData,
      false
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareSettingsSaveData(
      {
        departmentLetter,
        vendorName: departmentFilters?.vendorName || null,
        skuNumber: departmentFilters?._sku || null,
      },
      formData,
      store.id,
      user?.email,
      filteredData,
      selections
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SavePogList,
      data: updatedData,
    })
      .then((res) => {
        if (res?.data.length) {
          const postSaveUpdatedItems = postSettingsSaveUpdateData(
            formData,
            filteredData,
            res?.data || [],
            originalData,
            selections,
            skipPogSummaryUpdate(departmentFilters),
            departmentFilters
          );
          setData(saveUpdatedPogs(postSaveUpdatedItems));
          setFilteredData((prevState) => {
            const getUpdates = saveUpdatedPogs(postSaveUpdatedItems);
            const _partialUpdates = getUpdates(prevState);
            const updatedData = filterHandler(
              pogFilters,
              _partialUpdates,
              originalData,
              ENTITY_TYPES.planogram
            );
            return updatedData;
          });
          setOriginalData(saveUpdatedPogs(postSaveUpdatedItems));
          setSelectAll(false);
          setSelections([]);
          dispatch(showNotifier({ message: "POG details saved successfully" }));
          getTotal({ storeId: store?.id, type: "" });
          updateDuplicates();
        }
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const handleSettingsCreatePO = useStableCallback((details) => {
    const isValidSaveData = checkPogsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      originalData,
      null,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareCreatePOData(
      {
        departmentLetter,
        vendorName: departmentFilters?.vendorName || null,
        skuNumber: departmentFilters?._sku || null,
      },
      store.id,
      details,
      user?.email,
      selections
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.CreatePOGPONumber,
      data: updatedData,
    })
      .then((data) => {
        const postCreatePOUpdatedPogs = postCreatePOUpdateData(
          filteredData,
          data?.data,
          selections
        );
        setFilteredData(saveUpdatedPogs(postCreatePOUpdatedPogs, true));
        setData(saveUpdatedPogs(postCreatePOUpdatedPogs, true));
        setOriginalData(saveUpdatedPogs(postCreatePOUpdatedPogs, true));
        dispatch(
          showNotifier({
            message: "Saved changes to PO number type",
          })
        );
      })
      .catch((error) => {
        if (
          error?.response?.status === 500 &&
          error?.response?.data?.detail?.includes("UNIQUE KEY")
        ) {
          dispatch(
            showNotifier({
              message:
                "Can not generate PO number for selected POGs, since there are some duplicate items present in it.",
              severity: "error",
            })
          );
        } else if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not generate PO number for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
      });
  });

  const checkPOBeforeLocalUpdate = (details = []) => {
    const invalidPogs = [];
    const updatedPogs = details?.data ?? [];
    for (let i = 0; i < updatedPogs.length; i++) {
      const _pog = updatedPogs[i];
      if (_pog?.CountOfPOItems !== 0) {
        invalidPogs.push(_pog?.PogNumber);
      }
    }
    return invalidPogs;
  };

  const checkPOGenerated = useStableCallback(() => {
    let isAllPOGenerated = true;
    const poNumbers = filteredData
      ?.map((i) => {
        if (
          selections.includes(i?.PlanogramNumber) &&
          i?.__original?.Selected === singleValue.yes
        ) {
          return i.PlanogramNumber;
        }
        return undefined;
      })
      ?.filter((i) => !!i);
    if (poNumbers.length === selections.length) {
      isAllPOGenerated = true;
    } else {
      isAllPOGenerated = false;
    }
    return isAllPOGenerated;
  });

  const handleSettingsSaveDate = useStableCallback((releaseDate) => {
    const isValidSaveData = checkPogsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      originalData,
      null,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    if (!checkPOGenerated()) {
      return dispatch(
        showNotifier({
          message:
            "PO Number for some selected POGs are missing. Please generate PO Number.",
          severity: "error",
        })
      );
    }
    const updatedData = prepareSaveDatesData(
      {
        departmentLetter,
        vendorName: departmentFilters?.vendorName || null,
        skuNumber: departmentFilters?._sku || null,
      },
      releaseDate,
      store.id,
      user,
      selections
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SavePogReleaseDate,
      data: updatedData,
    })
      .then((res) => {
        const invalidPogs = checkPOBeforeLocalUpdate(res);
        const postSaveUpdatedItems = postSaveDatesUpdateData(
          releaseDate,
          filteredData,
          selections,
          res?.data ?? []
        );
        setFilteredData(saveUpdatedPogs(postSaveUpdatedItems));
        setData(saveUpdatedPogs(postSaveUpdatedItems));
        setOriginalData(saveUpdatedPogs(postSaveUpdatedItems));
        if (invalidPogs.length) {
          const _updatedPog =
            selections.length === 1
              ? filteredData?.find((i) =>
                  selections.includes(i.PlanogramNumber)
                )
              : {};
          if (
            selections.length === invalidPogs.length &&
            selections.length === 1 &&
            _updatedPog?.SKUs === Math.abs(res?.data[0]?.CountOfPOItems)
          ) {
            dispatch(
              showNotifier({
                message:
                  "Can not save the date for selected POGs. Please generate PO Number.",
                severity: "error",
              })
            );
          } else {
            dispatch(
              showNotifier({
                message:
                  "Release Dates saved successfully for valid POG items having PO number. Please generate PO Number for remaining items.",
                severity: "warning",
              })
            );
          }
        } else {
          dispatch(
            showNotifier({
              message: "Release Dates saved successfully.",
            })
          );
        }
        setSelectAll(false);
        setSelections([]);
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not save the date for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const handleSettingsDeleteDate = useStableCallback(() => {
    const isValidSaveData = checkPogsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      originalData,
      null,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareDeleteDatesData(
      {
        departmentLetter,
        vendorName: departmentFilters?.vendorName || null,
        skuNumber: departmentFilters?._sku || null,
      },
      store.id,
      user?.email,
      selections
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.DeletePOGReleaseDate,
      data: updatedData,
    })
      .then(() => {
        const postDeleteUpdatedItems = postDeleteDatesUpdateData(
          filteredData,
          selections
        );
        setFilteredData(saveUpdatedPogs(postDeleteUpdatedItems));
        setData(saveUpdatedPogs(postDeleteUpdatedItems));
        setOriginalData(saveUpdatedPogs(postDeleteUpdatedItems));
        setSelectAll(false);
        setSelections([]);
        dispatch(
          showNotifier({
            message: "Release Dates deleted successfully.",
          })
        );
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not delete the date for selected POGs.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const listRenderer = ({ index, key, style, parent }) => {
    let originalPog = {};
    const pog = filteredData[index] || {};
    for (let h = 0, len = originalData.length; h < len; h++) {
      const _pog = originalData[h] || {};
      if (_pog?.PlanogramNumber === pog?.PlanogramNumber) {
        originalPog = { ..._pog };
        break;
      }
    }
    const memoizedDataHandler = handleProcessData(
      pog?.PlanogramNumber,
      pog?.PlanogramName
    );
    const isDuplicateSelection =
      !pog.selection &&
      ![singleValue.yes, singleValue.existing].includes(pog?.Selected) &&
      ![singleValue.yes, singleValue.existing].includes(
        originalPog?.Selected
      ) &&
      selectedPogNames.includes(pog?.PlanogramName);

    const freezePog =
      !hasPermission({ scopes: [SCOPES.canEdit] }) ||
      store?.pending ||
      store?.isArchived ||
      store?.dataguideInProgress ||
      store?.isCompleted;

    return (
      <CellMeasurer
        key={key}
        cache={pogCache}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        {({ registerChild, measure }) => (
          <div style={style} key={key} ref={registerChild}>
            <CustomPOG
              pog={pog}
              isDuplicateSelection={isDuplicateSelection}
              onChange={measure}
              collapsedView={
                <CollapsedPogView
                  data={pog}
                  userEmail={user?.email}
                  storeId={store?.id}
                  noteSaveType={noteSaveType}
                  plannerNoteOptions={plannerNoteOptions}
                  retailerNoteOptions={retailerNoteOptions}
                  departmentLetter={departmentLetter}
                  handleProcessData={memoizedDataHandler}
                  freezePog={freezePog}
                  isDuplicateSelection={isDuplicateSelection}
                  plannerValue={getPlannerValue(pog)}
                  retailerValue={memoizedRetailerValue}
                />
              }
              expandedView={
                <ExpandedPogView
                  data={pog}
                  handleProcessData={memoizedDataHandler}
                  freezePog={freezePog}
                  releaseStatus={
                    !!pog?.ReleaseStatus ? (
                      <ReleaseStatusChip
                        status={pog?.ReleaseStatus === orderStatus.released}
                      />
                    ) : (
                      "-"
                    )
                  }
                  handlePogLevelSave={handlePogLevelSave(pog)}
                  isDuplicateSelection={isDuplicateSelection}
                />
              }
            />
          </div>
        )}
      </CellMeasurer>
    );
  };

  const hideSettings = useMemo(() => {
    const isSelectedItemFound = filteredData.find((i) =>
      selections.includes(i?.PlanogramNumber)
    );
    return isEmpty(selections) || !isSelectedItemFound;
  }, [selections, filteredData]);

  return (
    <>
      <Container id="page-container" maxWidth={false} sx={{ paddingY: 1 }}>
        <Stack spacing={1}>
          <Box sx={BreadcrumbHeaderSx}>
            <CustomBreadcrumb entityType={ENTITY_TYPES.planogram} />
            <StoreCreditSummary />
          </Box>
          <Total />
          {!isItemsDepartment && (
            <PogFilter
              disableFilters={pogsLoading}
              handleFilter={handleFilter}
              handleReset={handleFilterReset}
              handleSortChange={handleSortChange}
            />
          )}
          <CustomListContainer
            isResultPresent={!!filteredData.length}
            selectAll={validSelections.length ? selectAll : false}
            handleSelectAll={handleSelectAll}
            disableSelectAll
            resultLoading={pogsLoading}
            hideSelectAll
            entityType={ENTITY_TYPES.planogram}
            exportButton={
              <FileExport data={filteredData} type="osoPlanogram" />
            }
            stickyHeader={<StickyHeader entityType={ENTITY_TYPES.planogram} />}
            pagination={<></>}
            paginationTotals={<></>}
            resetAction={
              <PermissionsGate scopes={[SCOPES.canEdit]}>
                {!pogsLoading && !!filteredData.length && (
                  <>
                    <Button
                      variant="outlined"
                      sx={{ alignSelf: "flex-end" }}
                      onClick={handleItemReset}
                      size="small"
                      disabled={
                        store?.pending ||
                        store?.isArchived ||
                        store?.isCompleted ||
                        store?.dataguideInProgress
                      }
                    >
                      Reset
                    </Button>
                    <CommonDialog
                      open={showResetDialog}
                      title="Reset All Unsaved POGs"
                      content={
                        "Are you sure, you want to reset/revert all unsaved POG changes?"
                      }
                      type="yesNo"
                      handleClose={handleResetDialogClose}
                      handleDone={handleResetDialogDone}
                    />
                  </>
                )}
              </PermissionsGate>
            }
          >
            {filteredData.length ? (
              <div className="_virtual-container">
                <div className="_virtual-list-wrapper">
                  <InfiniteLoader
                    isRowLoaded={isRowLoaded}
                    loadMoreRows={loadMoreRows}
                    rowCount={filteredData.length}
                  >
                    {({ onRowsRendered, registerChild }) => (
                      <AutoSizer>
                        {({ width, height }) => (
                          <List
                            width={width}
                            height={height}
                            ref={registerChild}
                            deferredMeasurementCache={pogCache}
                            rowHeight={pogCache.rowHeight}
                            rowRenderer={listRenderer}
                            onRowsRendered={onRowsRendered}
                            rowCount={filteredData.length}
                            overscanRowCount={OVERSCAN_ROW_COUNT}
                          />
                        )}
                      </AutoSizer>
                    )}
                  </InfiniteLoader>
                </div>
              </div>
            ) : (
              <CustomNoResult
                entity={ENTITY_TYPES.planogram}
                loading={pogsLoading}
              />
            )}
          </CustomListContainer>
        </Stack>
        {!hideSettings && <CustomFloatingButton onClick={toggleDrawer(true)} />}
        <CustomDrawer open={isDrawerOpen} onClose={toggleDrawer(false)}>
          <SettingsForm
            onSave={handleSettingsSave}
            onCreatePONumbers={handleSettingsCreatePO}
            onSaveDates={handleSettingsSaveDate}
            onDeleteDates={handleSettingsDeleteDate}
            onClose={toggleDrawer(false)}
            isProjectCompleted={store?.isCompleted}
            isDataguideInProgress={store?.dataguideInProgress}
            isItemsDepartment={isItemsDepartment}
          />
        </CustomDrawer>
      </Container>
    </>
  );
}

export default memo(Pogs);
